import {
  AnimatePresence,
  motion,
  useInView,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
  FiArrowRight,
  FiPlay,
  FiSearch,
  FiStar,
  FiX,
  FiZap,
} from "react-icons/fi";
import { Link, useNavigate } from "react-router-dom";
import useMousePosition from "../../hooks/useMousePosition";

/* ─── Typing ─────────────────────────────────── */
const TypeWriter = ({ words }) => {
  const [idx, setIdx] = useState(0);
  const [text, setText] = useState("");
  const [del, setDel] = useState(false);

  useEffect(() => {
    const word = words[idx];
    const t = setTimeout(
      () => {
        if (!del) {
          setText(word.slice(0, text.length + 1));
          if (text.length + 1 === word.length)
            setTimeout(() => setDel(true), 2200);
        } else {
          setText(word.slice(0, text.length - 1));
          if (!text.length) {
            setDel(false);
            setIdx((p) => (p + 1) % words.length);
          }
        }
      },
      del ? 30 : 65,
    );
    return () => clearTimeout(t);
  }, [text, del, idx, words]);

  return (
    <>
      {text}
      <motion.span
        className="inline-block w-0.5 h-[0.75em] bg-current ml-0.5 align-middle rounded-full"
        animate={{ opacity: [1, 0] }}
        transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
      />
    </>
  );
};

/* ═══════════════════════════════════════════════ */
const Hero = () => {
  const [videoOpen, setVideoOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);
  const navigate = useNavigate();
  const mouse = useMousePosition();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });

  const { scrollY } = useScroll();
  const bgY = useSpring(useTransform(scrollY, [0, 800], [0, 200]), {
    stiffness: 40,
    damping: 20,
  });
  const mx = useSpring(mouse.x * 0.006, { stiffness: 15, damping: 12 });
  const my = useSpring(mouse.y * 0.006, { stiffness: 15, damping: 12 });

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim())
      navigate(`/websites?search=${encodeURIComponent(query.trim())}`);
  };

  const cats = [
    { name: "Technology", slug: "technology" },
    { name: "Design", slug: "design" },
    { name: "Education", slug: "education" },
    { name: "Dev Tools", slug: "devtools" },
    { name: "Finance", slug: "finance" },
    { name: "E-Commerce", slug: "ecommerce" },
  ];

  const avatars = [
    "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=50&h=50&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=50&h=50&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=50&h=50&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=50&h=50&fit=crop&crop=face",
    "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=50&h=50&fit=crop&crop=face",
  ];

  const anim = {
    wrap: {
      animate: { transition: { staggerChildren: 0.06, delayChildren: 0.15 } },
    },
    item: {
      initial: { opacity: 0, y: 30 },
      animate: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
      },
    },
  };

  return (
    <>
      <section ref={ref} className="relative overflow-hidden bg-[#050a18]">
        {/* ── Background ── */}
        <motion.div className="absolute inset-0" style={{ y: bgY }}>
          <video
            autoPlay
            muted
            loop
            playsInline
            poster="https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=80"
            className="absolute inset-0 w-full h-full object-cover scale-105"
          >
            <source
              src="https://cdn.coverr.co/videos/coverr-typing-on-a-laptop-5765/1080p.mp4"
              type="video/mp4"
            />
          </video>
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1451187580459-43490279c0fa?w=1920&h=1080&fit=crop&q=80')",
            }}
          />
        </motion.div>

        {/* ── Overlays ── */}
        <div className="absolute inset-0 bg-[#050a18]/[0.93]" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#050a18]/30 via-transparent to-[#050a18]" />

        {/* Ambient */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <motion.div
            className="absolute w-[800px] h-[800px] rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(99,102,241,0.04) 0%, transparent 60%)",
              x: mx,
              y: my,
              top: "0%",
              left: "10%",
            }}
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute w-[600px] h-[600px] rounded-full"
            style={{
              background:
                "radial-gradient(circle, rgba(139,92,246,0.025) 0%, transparent 60%)",
              bottom: "10%",
              right: "5%",
            }}
            animate={{ scale: [1, 1.1, 1], y: [0, -20, 0] }}
            transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        {/* Grain */}
        <div
          className="absolute inset-0 opacity-[0.012]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />

        {/* ── Content ── */}
        <div className="relative z-10 container-main">
          <motion.div
            className="max-w-[52rem] mx-auto text-center pt-28 sm:pt-32 lg:pt-40 pb-32 sm:pb-40 lg:pb-48"
            variants={anim.wrap}
            initial="initial"
            animate={inView ? "animate" : "initial"}
          >
            {/* Badge */}
            <motion.div
              variants={anim.item}
              className="mb-10 flex justify-center"
            >
              <Link
                to="/top-50"
                className="inline-flex items-center gap-2 text-[13px] text-white/30 hover:text-white/50 transition-colors duration-300 group"
              >
                <FiZap size={11} className="text-brand-400/60" />
                <span>Top 50 websites for 2024 are live</span>
                <FiArrowRight
                  size={11}
                  className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300"
                />
              </Link>
            </motion.div>

            {/* Headline */}
            <motion.h1
              variants={anim.item}
              className="text-[2.75rem] sm:text-[3.5rem] md:text-[4.25rem] lg:text-[5rem] font-bold font-display text-white leading-[1.05] tracking-[-0.02em] mb-8"
            >
              Discover the best
              <br />
              <span className="text-brand-400/80">
                <TypeWriter
                  words={["websites", "platforms", "tools", "resources"]}
                />
              </span>
              <br />
              <span className="text-white/20 text-[0.85em]">
                across the internet
              </span>
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              variants={anim.item}
              className="text-[15px] sm:text-base text-white/30 leading-[1.8] max-w-lg mx-auto mb-12"
            >
              A curated directory of{" "}
              <span className="text-white/55">1,847+ verified websites</span>{" "}
              with community reviews and expert ratings across 12 categories.
            </motion.p>

            {/* Search */}
            <motion.div variants={anim.item} className="max-w-lg mx-auto mb-10">
              <form onSubmit={handleSearch}>
                <div
                  className={`relative transition-all duration-500 rounded-xl
                    ${
                      focused
                        ? "bg-white/[0.07] ring-1 ring-white/[0.1]"
                        : "bg-white/[0.035] hover:bg-white/[0.055]"
                    }`}
                >
                  <FiSearch
                    className="absolute left-4 top-1/2 -translate-y-1/2 text-white/20"
                    size={16}
                  />
                  <input
                    type="text"
                    placeholder="Search websites, categories..."
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                    className="w-full bg-transparent pl-11 pr-28 py-3.5 text-sm text-white/80 placeholder-white/20 border-none focus:outline-none focus:ring-0"
                  />
                  <button
                    type="submit"
                    className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-white/[0.08] hover:bg-white/[0.14] text-white/60 hover:text-white/90 text-xs font-medium px-4 py-2 rounded-lg transition-all duration-200"
                  >
                    Search
                  </button>
                </div>
              </form>
            </motion.div>

            {/* Categories */}
            <motion.div
              variants={anim.item}
              className="flex flex-wrap justify-center gap-x-1 gap-y-2 mb-14"
            >
              {cats.map((c, i) => (
                <motion.div
                  key={c.slug}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 + i * 0.04 }}
                >
                  <Link
                    to={`/category/${c.slug}`}
                    className="px-3 py-1 text-[11px] text-white/20 hover:text-white/50 transition-colors duration-300"
                  >
                    {c.name}
                  </Link>
                </motion.div>
              ))}
            </motion.div>

            {/* Divider */}
            <motion.div
              variants={anim.item}
              className="w-10 h-px bg-white/[0.06] mx-auto mb-10"
            />

            {/* Bottom row */}
            <motion.div
              variants={anim.item}
              className="flex flex-col sm:flex-row items-center justify-center gap-8"
            >
              <div className="flex items-center gap-3">
                <div className="flex -space-x-1.5">
                  {avatars.map((src, i) => (
                    <motion.img
                      key={i}
                      src={src}
                      alt=""
                      className="w-6 h-6 rounded-full border border-[#050a18] object-cover"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{
                        delay: 1 + i * 0.06,
                        type: "spring",
                        stiffness: 300,
                        damping: 20,
                      }}
                    />
                  ))}
                </div>
                <div className="text-left">
                  <div className="flex items-center gap-px">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <FiStar
                        key={n}
                        size={8}
                        className="fill-white/20 text-white/20"
                      />
                    ))}
                  </div>
                  <p className="text-[10px] text-white/15 mt-px">28K+ users</p>
                </div>
              </div>
              <div className="hidden sm:block w-px h-4 bg-white/[0.04]" />
              <div className="flex items-center gap-5">
                <Link
                  to="/categories"
                  className="text-[12px] text-white/20 hover:text-white/45 transition-colors duration-300 flex items-center gap-1"
                >
                  Browse directory <FiArrowRight size={10} />
                </Link>
                <button
                  onClick={() => setVideoOpen(true)}
                  className="text-[12px] text-white/20 hover:text-white/45 transition-colors duration-300 flex items-center gap-1.5 group"
                >
                  <span className="w-5 h-5 rounded-full border border-white/[0.08] group-hover:border-white/[0.15] flex items-center justify-center transition-all duration-300">
                    <FiPlay size={7} className="ml-px fill-current" />
                  </span>
                  Watch demo
                </button>
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div
          className="absolute bottom-32 sm:bottom-36 lg:bottom-40 left-1/2 -translate-x-1/2 z-10"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.8 }}
        >
          <motion.div
            className="w-4 h-7 rounded-full border border-white/[0.06] flex justify-center pt-1.5"
            animate={{ opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 2.5, repeat: Infinity }}
          >
            <motion.div
              className="w-0.5 h-1.5 bg-white/15 rounded-full"
              animate={{ y: [0, 6, 0] }}
              transition={{
                duration: 1.5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </motion.div>
        </motion.div>

        {/* ═══════════════════════════════════════ */}
        {/* SHAPE DIVIDER — sits inside the hero   */}
        {/* ═══════════════════════════════════════ */}
        <div className="absolute bottom-0 left-0 right-0 z-20 pointer-events-none">
          {/* Option: Layered waves */}
          {/* Back wave — slightly darker */}
          <svg
            className="absolute bottom-0 w-full"
            viewBox="0 0 1440 120"
            fill="none"
            preserveAspectRatio="none"
            style={{ height: "80px" }}
          >
            <path
              d="M0 120L48 110C96 100 192 80 288 73.3C384 66.7 480 73.3 576 80C672 86.7 768 93.3 864 90C960 86.7 1056 73.3 1152 63.3C1248 53.3 1344 46.7 1392 43.3L1440 40V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0Z"
              fill="#f1f3f5"
            />
          </svg>

          {/* Front wave — page background color */}
          <svg
            className="absolute bottom-0 w-full"
            viewBox="0 0 1440 120"
            fill="none"
            preserveAspectRatio="none"
            style={{ height: "60px" }}
          >
            <path
              d="M0 120L60 106.7C120 93.3 240 66.7 360 60C480 53.3 600 66.7 720 73.3C840 80 960 80 1080 73.3C1200 66.7 1320 53.3 1380 46.7L1440 40V120H1380C1320 120 1200 120 1080 120C960 120 840 120 720 120C600 120 480 120 360 120C240 120 120 120 60 120H0Z"
              fill="#f8f9fa"
            />
          </svg>
        </div>
      </section>

      {/* ═══ VIDEO MODAL ═══ */}
      <AnimatePresence>
        {videoOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setVideoOpen(false)}
              className="absolute inset-0 bg-black/90 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.92 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.92 }}
              transition={{ type: "spring", damping: 25 }}
              className="relative w-full max-w-4xl z-10"
            >
              <button
                onClick={() => setVideoOpen(false)}
                className="absolute -top-10 right-0 text-white/40 hover:text-white/70 transition-colors"
              >
                <FiX size={20} />
              </button>
              <div className="bg-black rounded-2xl overflow-hidden aspect-video ring-1 ring-white/[0.08]">
                <iframe
                  width="100%"
                  height="100%"
                  src="https://www.youtube.com/embed/dQw4w9WgXcQ?autoplay=1"
                  title="Demo"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="w-full h-full"
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Hero;
